<div class="book" [class.book--search]="mode() === 'search'">
  @if (mode() !== 'search' && message()) {
    <p class="book__notice">
      <span aria-hidden="true">🛎️</span>
      {{ message() }}
    </p>
  }

  @if (mode() === 'search') {
    <header class="book__header book__header--search">
      <h1 class="book__title book__title--search">{{ titleText() }}</h1>
    </header>
    <p class="book__subtitle">{{ subtitleText() }}</p>
    @if (formError()) {
      <div class="book__validation" role="alert">
        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <circle cx="12" cy="12" r="9" stroke="currentColor" stroke-width="1.6" />
          <path d="M12 8v4" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" />
          <circle cx="12" cy="16" r="1" fill="currentColor" />
        </svg>
        <span>{{ formError() }}</span>
      </div>
    }
  } @else {
    <header class="book__header">
      <h1 class="book__title">Select a tee time</h1>
      <span class="book__price">{{ priceRange() }}</span>
    </header>
  }

  @if (mode() === 'search') {
    <div ngbDropdown #placeDropdown="ngbDropdown" class="book__picker" placement="bottom-start"
         (openChange)="onPlaceDropdownToggle($event)">
      <button type="button" class="book__date book__place-trigger" ngbDropdownToggle [disabled]="!interactive()">
        <span class="book__date-icon" aria-hidden="true">
          <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none">
            <path d="M12 21s-7-7.5-7-12a7 7 0 1 1 14 0c0 4.5-7 12-7 12z" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round" />
            <circle cx="12" cy="9" r="2.5" stroke="currentColor" stroke-width="1.6" />
          </svg>
        </span>
        @if (!placeTouched()) {
          <span class="book__date-label book__date-label--placeholder">
            {{ placeLoading() ? 'Detecting location…' : 'Select a place' }}
          </span>
        } @else {
          <span class="book__date-label">{{ placeDisplay() }}</span>
        }
        @if (placeTouched()) {
          <span class="book__clear" role="button" tabindex="0"
                (click)="clearPlace(); $event.stopPropagation()"
                (keydown.enter)="clearPlace(); $event.stopPropagation()"
                aria-label="Clear place">×</span>
        } @else {
          <svg class="book__caret" xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path d="m6 9 6 6 6-6" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
        }
      </button>
      <div ngbDropdownMenu class="book__menu book__menu--place">
        @if (canUseMyLocation()) {
          <button type="button" class="book__menu-item book__menu-item--use-location"
                  (click)="useMyLocation(); placeDropdown.close()">
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <circle cx="12" cy="12" r="3" stroke="currentColor" stroke-width="1.6" />
              <circle cx="12" cy="12" r="9" stroke="currentColor" stroke-width="1.6" />
              <path d="M12 2v3M12 19v3M2 12h3M19 12h3" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" />
            </svg>
            <span>Use my current location</span>
          </button>
        }
        <div class="book__place-search">
          <input type="text"
                 #placeInput
                 class="book__place-input"
                 placeholder="Search for a place"
                 [value]="placeQuery()"
                 (input)="onPlaceInput($any($event.target).value)"
                 (click)="$event.stopPropagation()" />
        </div>
        @if (placeLoading()) {
          <p class="book__place-status">Searching…</p>
        }
        @if (placeError()) {
          <p class="book__place-status book__place-status--error">{{ placeError() }}</p>
        }
        @for (s of placeResults(); track s.id) {
          <button type="button" class="book__menu-item"
                  (click)="selectPlaceFromResults(s); placeDropdown.close()">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="M12 21s-7-7.5-7-12a7 7 0 1 1 14 0c0 4.5-7 12-7 12z" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round" />
            </svg>
            <span class="book__place-result">{{ s.fullName }}</span>
          </button>
        }
      </div>
    </div>
  }

  <div ngbDropdown #dateDropdown="ngbDropdown" class="book__picker" placement="bottom-start">
    <button type="button" class="book__date"
            [class.book__date--readonly]="pickersReadonly()"
            ngbDropdownToggle [disabled]="!interactive() || pickersReadonly()">
      <span class="book__date-icon" aria-hidden="true">
        <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none">
          <rect x="3" y="5" width="18" height="16" rx="2" stroke="currentColor" stroke-width="1.6" />
          <path d="M3 9h18M8 3v4M16 3v4" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" />
          <circle cx="8" cy="14" r="1" fill="currentColor" />
          <circle cx="12" cy="14" r="1" fill="currentColor" />
          <circle cx="16" cy="14" r="1" fill="currentColor" />
        </svg>
      </span>
      @if (mode() === 'search' && !dateTouched()) {
        <span class="book__date-label book__date-label--placeholder">Select a date</span>
      } @else {
        <span class="book__date-label">{{ selectedDateLabel() }}</span>
      }
      @if (!pickersReadonly()) {
        <svg class="book__caret" xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path d="m6 9 6 6 6-6" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" />
        </svg>
      }
    </button>
    <div ngbDropdownMenu class="book__menu book__menu--calendar">
      <ngb-datepicker [startDate]="selectedNgbDate()"
                      [minDate]="minNgbDate()"
                      (dateSelect)="onDatepickerSelect($event); dateDropdown.close()"
                      [navigation]="'arrows'"
                      [showWeekNumbers]="false" />
    </div>
  </div>

  <div class="book__filters">
    <div ngbDropdown class="book__picker" placement="bottom-start">
      <button type="button" class="book__filter"
              [class.book__filter--readonly]="pickersReadonly()"
              ngbDropdownToggle [disabled]="!interactive() || pickersReadonly()">
        <span class="book__filter-icon" aria-hidden="true">
          <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none">
            <circle cx="9" cy="8" r="3.5" stroke="currentColor" stroke-width="1.6" />
            <circle cx="17" cy="10" r="2.5" stroke="currentColor" stroke-width="1.6" />
            <path d="M3 19c1-3 3.5-4.5 6-4.5s5 1.5 6 4.5" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" />
            <path d="M14 17c.7-1.6 1.9-2.5 3-2.5s2.3.9 3 2.5" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" />
          </svg>
        </span>
        @if (mode() === 'search' && !playersTouched()) {
          <span class="book__filter-label book__filter-label--placeholder">Players</span>
        } @else {
          <span class="book__filter-label">{{ playerCount() }} {{ playerCount() === 1 ? 'Player' : 'Players' }}</span>
        }
        @if (!pickersReadonly()) {
          <svg class="book__caret" xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path d="m6 9 6 6 6-6" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
        }
      </button>
      <div ngbDropdownMenu class="book__menu book__menu--list">
        @for (n of playerOptions; track n) {
          @let playerSelected = (mode() !== 'search' || playersTouched()) && playerCount() === n;
          <button type="button" ngbDropdownItem class="book__menu-item"
                  [class.book__menu-item--active]="playerSelected"
                  (click)="selectPlayerCount(n)">
            <span>{{ n }} {{ n === 1 ? 'Player' : 'Players' }}</span>
            @if (playerSelected) {
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path d="m5 12 5 5L20 7" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
              </svg>
            }
          </button>
        }
      </div>
    </div>

    <div ngbDropdown class="book__picker" placement="bottom-start">
      <button type="button" class="book__filter"
              [class.book__filter--readonly]="pickersReadonly()"
              ngbDropdownToggle [disabled]="!interactive() || pickersReadonly()">
        <span class="book__filter-icon" aria-hidden="true">
          <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none">
            <path d="M6 3v18" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" />
            <path d="M6 4h12l-3 4 3 4H6" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round" />
          </svg>
        </span>
        @if (mode() === 'search' && !holesTouched()) {
          <span class="book__filter-label book__filter-label--placeholder">Holes</span>
        } @else {
          <span class="book__filter-label">{{ holesCount() }} Holes</span>
        }
        @if (!pickersReadonly()) {
          <svg class="book__caret" xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path d="m6 9 6 6 6-6" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
        }
      </button>
      <div ngbDropdownMenu class="book__menu book__menu--list">
        @for (n of holesOptions; track n) {
          @let holesSelected = (mode() !== 'search' || holesTouched()) && holesCount() === n;
          <button type="button" ngbDropdownItem class="book__menu-item"
                  [class.book__menu-item--active]="holesSelected"
                  (click)="selectHolesCount(n)">
            <span>{{ n }} Holes</span>
            @if (holesSelected) {
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path d="m5 12 5 5L20 7" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
              </svg>
            }
          </button>
        }
      </div>
    </div>
  </div>

  @if (requeryEnabled && (mode() === 'detail' || mode() === 'picker') && courseParamsChanged()) {
    <button type="button" class="book__cta book__cta--filled book__cta--update"
            [disabled]="!interactive()"
            (click)="refreshCourseDetails()">
      Update tee times
    </button>
  }

  @if (mode() === 'picker' && recommendedTimes().length) {
  <section class="book__times">
    <header class="book__times-header">
      <span class="book__times-icon" aria-hidden="true">
        <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none">
          <circle cx="12" cy="12" r="9" stroke="currentColor" stroke-width="1.6" />
          <path d="M12 7v5l3 2" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" />
        </svg>
      </span>
      <h2 class="book__times-title">Recommended times</h2>
    </header>
    <div class="book__pills">
      @for (slot of recommendedTimes(); track slot.apptTime || slot.time) {
        <button type="button" class="book__pill"
                [class.book__pill--selected]="selectedTime() === (slot.apptTime || slot.time)"
                [class.book__pill--unavailable]="slot.available === false"
                [disabled]="!interactive()"
                (click)="selectTime(slot)">
          {{ slot.time }}
        </button>
      }
    </div>
  </section>
  }

  @if (mode() === 'detail' && !hasSlots()) {
    @let parts = noSlotsParts();
    <div class="book__no-slots">
      <span class="book__no-slots-icon" aria-hidden="true">
        <svg xmlns="http://www.w3.org/2000/svg" width="34" height="34" viewBox="0 0 24 24" fill="none">
          <rect x="3" y="5" width="18" height="16" rx="2" stroke="currentColor" stroke-width="1.6" />
          <path d="M3 9h18M8 3v4M16 3v4" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" />
          <path d="m10 14 4 4M14 14l-4 4" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" opacity="0.55" />
        </svg>
      </span>
      @if (parts[0]) {
        <p class="book__no-slots-title">{{ parts[0] }}</p>
      }
      @if (parts.length > 1 && parts[1]) {
        <p class="book__no-slots-subtitle">{{ parts[1] }}</p>
      }
    </div>
  }

  @if (mode() === 'picker' && !pickerHasTimes()) {
    @let pickerNoSlotsParts = noSlotsParts();
    <div class="book__no-slots">
      <span class="book__no-slots-icon" aria-hidden="true">
        <svg xmlns="http://www.w3.org/2000/svg" width="34" height="34" viewBox="0 0 24 24" fill="none">
          <rect x="3" y="5" width="18" height="16" rx="2" stroke="currentColor" stroke-width="1.6" />
          <path d="M3 9h18M8 3v4M16 3v4" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" />
          <path d="m10 14 4 4M14 14l-4 4" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" opacity="0.55" />
        </svg>
      </span>
      @if (pickerNoSlotsParts[0]) {
        <p class="book__no-slots-title">{{ pickerNoSlotsParts[0] }}</p>
      }
      @if (pickerNoSlotsParts.length > 1 && pickerNoSlotsParts[1]) {
        <p class="book__no-slots-subtitle">{{ pickerNoSlotsParts[1] }}</p>
      }
    </div>
  }

  @let showTimeGrids = (mode() === 'picker' && showAllTimes()) || (mode() === 'detail' && hasSlots());
  @if (showTimeGrids && morningTimes().length) {
    <section class="book__times">
      <header class="book__times-header">
        <span class="book__times-icon" aria-hidden="true">
          <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none">
            <path d="M12 2V10" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
            <path d="M4.93 10.93l1.41 1.41" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
            <path d="M2 18h2" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
            <path d="M20 18h2" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
            <path d="M19.07 10.93l-1.41 1.41" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
            <path d="M22 22H2" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
            <path d="M8 6l4-4 4 4" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
            <path d="M16 18a4 4 0 0 0-8 0" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
        </span>
        <h2 class="book__times-title">Morning times</h2>
      </header>
      <div class="book__pills">
        @for (slot of morningTimes(); track slot.apptTime || slot.time) {
          <button type="button" class="book__pill"
                  [class.book__pill--selected]="selectedTime() === (slot.apptTime || slot.time)"
                  [class.book__pill--unavailable]="slot.available === false"
                  [disabled]="!interactive()"
                  (click)="selectTime(slot)">
            {{ slot.time }}
          </button>
        }
      </div>
    </section>
  }

  @if (showTimeGrids && afternoonTimes().length) {
    <section class="book__times">
      <header class="book__times-header">
        <span class="book__times-icon" aria-hidden="true">
          <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none">
            <path d="M12 10V2" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
            <path d="M4.93 10.93l1.41 1.41" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
            <path d="M2 18h2" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
            <path d="M20 18h2" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
            <path d="M19.07 10.93l-1.41 1.41" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
            <path d="M22 22H2" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
            <path d="M16 6l-4 4-4-4" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
            <path d="M16 18a4 4 0 0 0-8 0" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
        </span>
        <h2 class="book__times-title">Afternoon times</h2>
      </header>
      <div class="book__pills">
        @for (slot of afternoonTimes(); track slot.apptTime || slot.time) {
          <button type="button" class="book__pill"
                  [class.book__pill--selected]="selectedTime() === (slot.apptTime || slot.time)"
                  [class.book__pill--unavailable]="slot.available === false"
                  [disabled]="!interactive()"
                  (click)="selectTime(slot)">
            {{ slot.time }}
          </button>
        }
      </div>
    </section>
  }

  @if (mode() === 'search') {
    <div class="book__period-row">
      <button type="button" class="book__period"
              [class.book__period--selected]="morningSelected()"
              [disabled]="!interactive()"
              (click)="toggleMorning()">
        <span class="book__period-icon" aria-hidden="true">
          <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none">
            <path d="M12 2V10" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
            <path d="M4.93 10.93l1.41 1.41" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
            <path d="M2 18h2" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
            <path d="M20 18h2" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
            <path d="M19.07 10.93l-1.41 1.41" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
            <path d="M22 22H2" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
            <path d="M8 6l4-4 4 4" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
            <path d="M16 18a4 4 0 0 0-8 0" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
        </span>
        <span class="book__period-label">Morning</span>
      </button>
      <button type="button" class="book__period"
              [class.book__period--selected]="afternoonSelected()"
              [disabled]="!interactive()"
              (click)="toggleAfternoon()">
        <span class="book__period-icon" aria-hidden="true">
          <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none">
            <path d="M12 10V2" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
            <path d="M4.93 10.93l1.41 1.41" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
            <path d="M2 18h2" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
            <path d="M20 18h2" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
            <path d="M19.07 10.93l-1.41 1.41" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
            <path d="M22 22H2" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
            <path d="M16 6l-4 4-4-4" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
            <path d="M16 18a4 4 0 0 0-8 0" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
        </span>
        <span class="book__period-label">Afternoon</span>
      </button>
    </div>

    <button type="button" class="book__cta book__cta--search"
            [class.book__cta--filled]="canSearch()"
            [class.book__cta--disabled]="!canSearch()"
            [disabled]="!interactive() || !canSearch()"
            (click)="searchTeeTimes()">
      Search
    </button>
  } @else if (mode() === 'picker') {
    @if (pickerHasTimes()) {
      <div class="book__view-all-row">
        <button type="button" class="book__view-all" [disabled]="!interactive()" (click)="toggleAllTimes()">
          {{ showAllTimes() ? 'Hide all tee times' : 'View all tee times' }}
        </button>
      </div>

      @let unavailableSelectedPicker = isSelectedPillUnavailable();
      <!-- Alert / waitlist are offered only when the selected slot is unavailable. -->
      @if (unavailableSelectedPicker) {
        <p class="book__waitlist-text">
          That slot isn't available — we can alert you when it opens or add you to the waitlist.
        </p>
      }

      @if (interactive()) {
        <!-- Book is enabled only for a real, bookable selection: an available pill that
             actually carries an apptTime. This prevents a silently-dead button. -->
        @let canBookPicker = !unavailableSelectedPicker && canBookSelectedPill();
        <button type="button" class="book__cta book__cta--filled"
                [class.book__cta--disabled]="!canBookPicker"
                [disabled]="!canBookPicker"
                (click)="bookTeeTime()">
          Book tee time
        </button>
        @if (unavailableSelectedPicker) {
          <button type="button" class="book__cta book__cta--outline" (click)="requestWaitlistForm()">
            Yes, add me to the waitlist
          </button>
          <button type="button" class="book__cta book__cta--outline" (click)="requestAlertForm()">
            Alert me
          </button>
        }
      }
    } @else if (interactive()) {
      <!-- Picker returned no bookable times (e.g. a rebook date with no matching slots).
           Offer the waitlist when the backend allows it. Alert is intentionally omitted:
           it needs a specific appointmentTime, which an empty picker doesn't carry. -->
      @if (availability()?.canWaitlist !== false) {
        <button type="button" class="book__cta book__cta--outline" (click)="requestWaitlistForm()">
          Add me to waitlist
        </button>
      }
    }
  } @else if (mode() === 'detail') {
    @let avail = availability();
    @let unavailableSelected = isSelectedPillUnavailable();
    @if (hasSlots()) {
      @let canBookNow = !!selectedPill() && !unavailableSelected && (avail === null || avail.canBook !== false);
      <button type="button" class="book__cta book__cta--search"
              [class.book__cta--filled]="canBookNow"
              [class.book__cta--disabled]="!canBookNow"
              [disabled]="!interactive() || !canBookNow"
              (click)="bookTeeTime()">
        Book tee time
      </button>

      <!-- Alert / waitlist appear when the user has selected a slot the backend
           flagged as unavailable. Available selections hide them. -->
      @if (unavailableSelected) {
        <p class="book__waitlist-text">
          That slot isn't available — we can alert you when it opens or add you to the waitlist.
        </p>
        <button type="button" class="book__cta book__cta--outline"
                [disabled]="!interactive()"
                (click)="requestWaitlistForm()">
          Add me to waitlist
        </button>
        <button type="button" class="book__cta book__cta--outline"
                [disabled]="!interactive()"
                (click)="requestAlertForm()">
          Alert me
        </button>
      }
    } @else {
      <!-- No slots at all for this date: only the waitlist is offered. Alert
           needs a specific tee time, and there isn't one here to alert on. -->
      <p class="book__waitlist-text">
        No tee times available — we can add you to the waitlist for this course.
      </p>
      <button type="button" class="book__cta book__cta--outline"
              [disabled]="!interactive()"
              (click)="requestWaitlistForm()">
        Add me to waitlist
      </button>
    }
  }
</div>
